import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPlant, createPlant, updatePlant, getUploadUrl } from '../api';
import { CATEGORIES } from '../data/categories';

const SEASONS = ['spring', 'summer', 'fall', 'winter'];
const GROWTH_STAGES = ['seedling', 'vegetative', 'budding', 'flowering', 'fruiting', 'dormant'];

export default function AdminPlantForm({ token }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    plantName: '', scientificName: '', category: 'perennials',
    plantingDate: '', gardenLocation: '', careInstructions: '', growthNotes: '', tags: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploadSeason, setUploadSeason] = useState('spring');
  const [uploadStage, setUploadStage] = useState('');
  const [dragover, setDragover] = useState(false);

  useEffect(() => {
    if (isEdit) {
      getPlant(id).then(data => {
        if (data && !data.error) {
          setForm({
            plantName: data.plantName || '',
            scientificName: data.scientificName || '',
            category: data.category || 'perennials',
            plantingDate: data.plantingDate || '',
            gardenLocation: data.gardenLocation || '',
            careInstructions: data.careInstructions || '',
            growthNotes: data.growthNotes || '',
            tags: (data.tags || []).join(', '),
          });
        }
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const data = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (isEdit) {
        await updatePlant(id, data, token);
      } else {
        await createPlant(data, token);
      }
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  const handleFiles = async (files) => {
    if (!isEdit) { setError('Save the plant first, then upload photos.'); return; }
    const fileList = Array.from(files).filter(f => f.type.startsWith('image/'));
    for (const file of fileList) {
      const uploadEntry = { name: file.name, progress: 0, status: 'uploading' };
      setUploads(prev => [...prev, uploadEntry]);
      try {
        const { uploadUrl } = await getUploadUrl({
          plantId: id, filename: file.name, contentType: file.type,
          season: uploadSeason, growthStage: uploadStage,
        }, token);
        await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        setUploads(prev => prev.map(u => u.name === file.name ? { ...u, progress: 100, status: 'done' } : u));
      } catch {
        setUploads(prev => prev.map(u => u.name === file.name ? { ...u, status: 'error' } : u));
      }
    }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragover(false); handleFiles(e.dataTransfer.files); };

  return (
    <div className="form-page">
      <div className="page-head">
        <p className="eyebrow">Admin</p>
        <h1 className="page-title">{isEdit ? 'Edit plant record' : 'Add a plant record'}</h1>
      </div>
      {error && <p className="form-error form-error--banner" role="alert">{error}</p>}

      <form onSubmit={handleSubmit} className="panel">
        <div className="form-group">
          <label htmlFor="plantName">Plant name *</label>
          <input id="plantName" name="plantName" value={form.plantName} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="scientificName">Scientific name</label>
          <input id="scientificName" name="scientificName" value={form.scientificName} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select id="category" name="category" value={form.category} onChange={handleChange}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="plantingDate">Planting date</label>
          <input id="plantingDate" name="plantingDate" type="date" value={form.plantingDate} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="gardenLocation">Location in garden</label>
          <input id="gardenLocation" name="gardenLocation" value={form.gardenLocation} onChange={handleChange} placeholder="Front yard, Bed A, Greenhouse" />
        </div>
        <div className="form-group">
          <label htmlFor="careInstructions">Care instructions</label>
          <textarea id="careInstructions" name="careInstructions" value={form.careInstructions} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="growthNotes">Growth notes</label>
          <textarea id="growthNotes" name="growthNotes" value={form.growthNotes} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="tags">Tags</label>
          <input id="tags" name="tags" value={form.tags} onChange={handleChange} placeholder="organic, heirloom, shade-loving" />
          <p className="hint">Comma-separated.</p>
        </div>
        <div className="page-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update record' : 'Create record'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin')}>Cancel</button>
        </div>
      </form>

      {/* Photo upload (edit mode only) */}
      {isEdit && (
        <div className="panel" style={{ marginTop: 'var(--s-6)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--s-4)' }}>Upload photographs</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="uploadSeason">Season</label>
              <select id="uploadSeason" value={uploadSeason} onChange={e => setUploadSeason(e.target.value)}>
                {SEASONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="uploadStage">Growth stage</label>
              <select id="uploadStage" value={uploadStage} onChange={e => setUploadStage(e.target.value)}>
                <option value="">Select...</option>
                {GROWTH_STAGES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div
            className={`upload-area ${dragover ? 'dragover' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            aria-label="Upload photos by clicking or dragging files here"
          >
            <p><strong>Drag and drop photographs here, or click to browse</strong></p>
            <p className="upload-area__hint">JPG, PNG or WebP</p>
            <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
          </div>
          {uploads.length > 0 && (
            <ul className="upload-list">
              {uploads.map((u, i) => (
                <li key={i} className="upload-list__item">
                  <span className="upload-list__name">{u.name}</span>
                  <span className={`upload-list__status is-${u.status}`}>
                    {u.status === 'done' ? 'Uploaded' : u.status === 'error' ? 'Failed' : 'Uploading'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
